import React from 'react';
import {FormTab} from "./FormTab";
import {connect} from "react-redux";
import {addDetailWindow, removeDetailWindow, setDetailWindow} from "../../actions/details";
import {setDirty} from "../../actions/dirty";
import {setEditContext, clearEditContext} from "qwc2/actions/editing";
import {removeLayer} from "qwc2/actions/layers";
import axios from "axios";
import ConfigUtils from "qwc2/utils/ConfigUtils";
import FeatureUtils from "../../utils/FeatureUtils";
import LocaleUtils from "qwc2/utils/LocaleUtils";
import VectorLayerUtils from "qwc2/utils/VectorLayerUtils";

class FeatureformTab extends FormTab {

    isCreate() {
        return !this.props.feature.properties || !this.props.feature.properties.id;
    }

    getGeomEditContextId() {
        return 'geomedit-' + this.props.windowId;
    }

    toggleGeometryEdit = () => {
        const contextId = this.getGeomEditContextId();
        if (this.props.geomEditActive) {
            this.props.clearEditContext(contextId);
        } else {
            // existing features are loaded from the REST backend in EPSG:4326 (never tagged as such) and
            // must be reprojected into the map's view projection for the edit interaction to find them on
            // screen; a not-yet-saved new feature's geometry is already in the map projection (see AddFeature.jsx)
            const geometry = this.isCreate() ?
                this.props.feature.geometry :
                VectorLayerUtils.reprojectGeometry(this.props.feature.geometry, 'EPSG:4326', this.props.mapProjection);
            this.props.setEditContext(contextId, {
                action: 'Pick',
                // qwc2's editing reducer derives geomType and permissions from the editConfig
                editConfig: {geomType: geometry.type, permissions: {creatable: true, updatable: true}},
                feature: {type: 'Feature', geometry: geometry, properties: {}},
                changed: false
            });
        }
    }

    renderField(field) {
        if (field.type === 'geometry') {
            return this.renderGeometryField(field);
        }
        return super.renderField(field);
    }

    renderGeometryField(field) {
        const active = this.props.geomEditActive;
        return (
            <div className="feature-details-field" key={field.name}>
                <label>{field.label}</label>
                <div className="feature-details-input feature-details-geometry">
                    <button className="button" onClick={this.toggleGeometryEdit} type="button">
                        {active ? LocaleUtils.tr("featuredetails.geometrystop") : LocaleUtils.tr("featuredetails.geometrystart")}
                    </button>
                    {this.geometryEdited &&
                        <span className="feature-details-geometry-status">{LocaleUtils.tr("featuredetails.geometrychanged")}</span>
                    }
                    {active &&
                        <span className="feature-details-geometry-hint">{LocaleUtils.tr("featuredetails.geometryhint")}</span>
                    }
                </div>
            </div>
        );
    }

    // applies a geometry moved via the window's "edit geometry" control (see FeatureDetail.jsx) into the
    // pending save payload - dynamicConfig isn't React state, so a re-render isn't needed to pick this up
    // at submit time, but forceUpdate keeps anything else reading getConfig() in sync too
    applyGeometryEdit(context) {
        if (!context || !context.changed || !context.feature || !context.feature.geometry || !this.dynamicConfig) {
            return;
        }
        const isCreate = this.isCreate();
        // reproject back to EPSG:4326, the CRS the REST backend expects, for an existing feature; a
        // not-yet-saved new feature's geometry stays in the map projection (see toggleGeometryEdit)
        const geometry = isCreate ?
            context.feature.geometry :
            VectorLayerUtils.reprojectGeometry(context.feature.geometry, this.props.mapProjection, 'EPSG:4326');
        this.dynamicConfig = {
            ...this.dynamicConfig,
            data: {
                ...this.dynamicConfig.data,
                type: 'Feature',
                geometry: geometry
            },
            headers: {...this.dynamicConfig.headers}
        };
        const contentCrs = FeatureUtils.crsToContentCrsHeader(isCreate ? this.props.mapProjection : 'EPSG:4326');
        if (contentCrs) {
            this.dynamicConfig.headers['Content-Crs'] = contentCrs;
        }
        this.geometryEdited = true;
        this.props.setDirty(this.props.windowId, true);
        // a stale search-result marker (SearchBox's own "searchselection" layer) would otherwise keep
        // showing the pre-edit position - it isn't kept in sync by anything, so just clear it
        this.props.removeLayer("searchselection");
        this.forceUpdate();
    }

    componentDidUpdate(prevProps) {
        if (this.props.geomEditContext !== prevProps.geomEditContext) {
            this.applyGeometryEdit(this.props.geomEditContext);
        }
    }

    triggerSuccess(emptyFormData, result) {
        if (this.geometryEdited) {
            this.props.clearEditContext(this.getGeomEditContextId());
            this.geometryEdited = false;
        }
        super.triggerSuccess(emptyFormData, result);
    }

    buildConfigFromSchema(schema) {
        const feature = this.props.feature;
        const isCreate = this.isCreate();
        const required = schema.required || [];
        const properties = schema.properties || {};
        const data = {
            properties: {}
        };
        if (isCreate) {
            data.type = 'Feature';
            data.geometry = feature.geometry;
        }
        const fields = [];

        Object.keys(properties).forEach(name => {
            const prop = properties[name];
            if (name === 'id' || name === 'geometry' || prop.readOnly || prop.type === 'array') {
                return;
            }

            const field = {
                name: 'properties.' + name,
                label: prop.description || name,
                type: 'text',
                omitEmptyValue: true,
                validate: {
                    required: required.includes(name)
                }
            };
            if (Array.isArray(prop.enum)) {
                field.type = 'select';
                field.options = prop.enum.map(value => ({label: value, value: value}));
            } else if (Array.isArray(prop.oneOf)) {
                field.type = 'select';
                field.options = prop.oneOf.map(option => ({label: option.title, value: option.const}));
            } else if (prop['x-ogc-codelistUri']) {
                field.type = 'autocomplete';
                field.options = prop['x-ogc-codelistUri'];
            } else if (prop.type === 'boolean') {
                field.type = 'select';
                field.options = [
                    {label: 'Ja', value: true},
                    {label: 'Nein', value: false}
                ];
            } else if (prop.type === 'integer') {
                field.type = 'integer';
            } else if (prop.type === 'number') {
                field.type = 'decimal';
            } else if (prop.format === 'date-time' || prop.format === 'date') {
                field.type = 'date';
            }
            fields.push(field);

            let value = feature.properties ? feature.properties[name] : undefined;
            if (typeof value === 'undefined' || value === null) {
                value = '';
            }
            data.properties[name] = value;
        });

        if (feature.geometry) {
            fields.unshift({
                name: 'geometry',
                label: LocaleUtils.tr("featuredetails.geometry"),
                type: 'geometry',
                omitEmptyValue: false,
                validate: {required: false}
            });
        }

        const headers = {
            'Authorization': 'Bearer {token}',
            'Content-Type': isCreate ? 'application/geo+json' : 'application/merge-patch+json'
        };
        if (isCreate) {
            const contentCrs = FeatureUtils.crsToContentCrsHeader(feature.geometryCrs);
            if (contentCrs) {
                headers['Content-Crs'] = contentCrs;
            }
        } else {
            headers['If-Match'] = '*';
        }

        const successActions = [
            {
                type: 'message',
                message: isCreate ? 'Das Anlegen war erfolgreich.' : 'Das Speichern war erfolgreich.'
            }
        ];
        if (isCreate) {
            successActions.push({type: 'closewindow'});
        } else {
            successActions.push({type: 'updatefeature'});
        }

        return {
            buttonLabel: 'Speichern',
            success: successActions,
            error: [
                {
                    type: 'message',
                    message: isCreate ? 'Das Anlegen ist fehlgeschlagen.' : 'Das Speichern ist fehlgeschlagen.'
                }
            ],
            method: isCreate ? 'post' : 'patch',
            headers: headers,
            fields: fields,
            data: data
        };
    }

    async componentDidMount() {
        const url = this.getUrl();

        const schemaUrl = url.replaceAll('items/{uuid}', 'schema?f=json');
        try {
            const response = await axios.get(schemaUrl);
            this.dynamicConfig = this.buildConfigFromSchema(response.data);
        } catch (error) {
            console.log("Error loading schema *** : " + error);
            this.dynamicConfig = {fields: [], success: [], error: []};
        }
        this.applyGeometryEdit(this.props.geomEditContext);
        super.componentDidMount();
    }

    getUrl() {
        if (this.props.tab.url) {
            return this.props.tab.url;
        }
        const objectType = FeatureUtils.getObjectType(this.props.feature);
        return ConfigUtils.getConfigProp("featureServiceUrl") + '/' + objectType + '/items/{uuid}';
    }

    getConfig() {
        return this.dynamicConfig;
    }
}

const selector = (state, ownProps) => ({
    geomEditContext: state.editing.contexts['geomedit-' + ownProps.windowId],
    geomEditActive: state.editing.currentContext === ('geomedit-' + ownProps.windowId),
    mapProjection: state.map.projection
});

export default connect(selector, {
    setDetailWindow: setDetailWindow,
    addDetailWindow: addDetailWindow,
    removeDetailWindow: removeDetailWindow,
    setDirty: setDirty,
    setEditContext: setEditContext,
    clearEditContext: clearEditContext,
    removeLayer: removeLayer
})(FeatureformTab);
